import { useCallback, useEffect, useState } from 'react';
import { UserIdentity } from '@backstage/core-components';
import { IdentityApi, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

type SignInPageProps = {
  onSignInSuccess(identityApi: IdentityApi): void;
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)',
    padding: theme.spacing(3),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 420,
    padding: theme.spacing(6, 5),
    borderRadius: 16,
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(12px)',
  },
  logo: {
    height: 72,
    width: 'auto',
    marginBottom: theme.spacing(4),
  },
  title: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '1.5rem',
    textAlign: 'center',
    lineHeight: 1.3,
    marginBottom: theme.spacing(1),
  },
  tagline: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '0.9rem',
    textAlign: 'center',
    marginBottom: theme.spacing(5),
  },
  divider: {
    width: '100%',
    border: 'none',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    marginBottom: theme.spacing(4),
  },
  button: {
    width: '100%',
    padding: theme.spacing(1.5, 3),
    borderRadius: 8,
    textTransform: 'none',
    fontSize: '0.95rem',
    fontWeight: 600,
    background: '#7df3e1',
    color: '#0f172a',
    letterSpacing: 0.3,
    '&:hover': {
      background: '#5ddecb',
    },
    '&:disabled': {
      background: 'rgba(125, 243, 225, 0.4)',
      color: 'rgba(15, 23, 42, 0.6)',
    },
  },
  msIcon: {
    width: 18,
    height: 18,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: {
    marginTop: theme.spacing(2),
    color: '#f87171',
    fontSize: '0.85rem',
    textAlign: 'center',
  },
  footer: {
    marginTop: theme.spacing(5),
    color: 'rgba(255, 255, 255, 0.2)',
    fontSize: '0.75rem',
    textAlign: 'center',
  },
}));

// Microsoft logo SVG (official 4-square grid)
const MicrosoftIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export const CustomSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const classes = useStyles();
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Auto sign-in: silently check for an existing valid session on load
  useEffect(() => {
    microsoftAuth
      .getBackstageIdentity({ optional: true })
      .then(async identity => {
        if (identity) {
          onSignInSuccess(
            UserIdentity.create({
              identity: identity.identity,
              authApi: microsoftAuth,
              profile: await microsoftAuth.getProfile(),
            }),
          );
        } else {
          setChecking(false);
        }
      })
      .catch(() => {
        // No existing session — show the sign-in button
        setChecking(false);
      });
  }, [microsoftAuth, onSignInSuccess]);

  const handleSignIn = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const identity = await microsoftAuth.getBackstageIdentity({
        optional: false,
      });
      if (!identity) {
        throw new Error('No identity returned from sign-in.');
      }
      onSignInSuccess(
        UserIdentity.create({
          identity: identity.identity,
          authApi: microsoftAuth,
          profile: await microsoftAuth.getProfile(),
        }),
      );
    } catch (err: any) {
      setError(err?.message ?? 'Sign-in failed. Please try again.');
      setLoading(false);
    }
  }, [microsoftAuth, onSignInSuccess]);

  if (checking) {
    return (
      <div className={classes.root}>
        <CircularProgress style={{ color: '#7df3e1' }} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <img
          src="/logo-craftview2-removebg.png"
          alt="Craftview Logo"
          className={classes.logo}
        />
        <Typography className={classes.title}>Backstage by Craftview</Typography>
        <Typography className={classes.tagline}>
          Your Internal Developer Portal
        </Typography>

        <hr className={classes.divider} />

        <Button
          className={classes.button}
          onClick={handleSignIn}
          disabled={loading}
          disableElevation
          variant="contained"
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: '#0f172a' }} />
          ) : (
            <span className={classes.buttonContent}>
              <MicrosoftIcon className={classes.msIcon} />
              Sign in with Craftview-SSO
            </span>
          )}
        </Button>

        {error && <Typography className={classes.error}>{error}</Typography>}

        <Typography className={classes.footer}>
          Powered by Backstage
        </Typography>
      </div>
    </div>
  );
};
